import { useCallback, useEffect, useState } from 'react';

interface FieldConfig {
  clearRootError?: boolean;
  id: string;
}

type ErrorState<T> = {
  [K in keyof T as `${string & K}`]: boolean;
};

export const useFieldErrorVisibility = <T extends Record<string, unknown>>(
  fields: FieldConfig[],
  clearErrors?: (field: keyof T) => void
) => {
  const [errorState, setErrorState] = useState<ErrorState<T>>(
    {} as ErrorState<T>
  );

  useEffect(() => {
    const listeners: { fieldId: string; listener: () => void }[] = [];

    fields.forEach(({ clearRootError, id }) => {
      const listener = () => {
        if (clearRootError && clearErrors) {
          clearErrors('root');
        }
        setErrorState((prev) => ({ ...prev, [`${id}`]: false }));
      };
      document.getElementById(id)?.addEventListener('keydown', listener);
      listeners.push({ fieldId: id, listener });
    });

    return () => {
      listeners.forEach(({ fieldId, listener }) => {
        document
          .getElementById(fieldId)
          ?.removeEventListener('keydown', listener);
      });
    };
  }, [fields, clearErrors]);

  const setShowError = useCallback((id: string, value: boolean) => {
    setErrorState((prev) => ({ ...prev, [`${id}`]: value }));
  }, []);

  const getShowError = useCallback(
    (id: string) => {
      return errorState[`${id}`];
    },
    [errorState]
  );

  return {
    getShowError,
    setShowError,
  };
};
